/**
 * WS-HF — run ONE generation on the operator's own Higgsfield subscription.
 *
 * The cloud still owns the JOB. This function only changes who executes it:
 *
 *  1. `enqueueGeneration(..., { executor: "device" })` — the cloud creates the record, stores
 *     the design, resolves the prompt, and does NOT dispatch it to a worker.
 *  2. `GET /api/generation-jobs/{id}` → the prompt and the three input links, `inputUrl`
 *     (the case reference), `productUrl` (the real phone, optional) and `viewUrl` (the view
 *     reference). Falling back to `media_upload` when there is no `inputUrl` is deliberately
 *     NOT attempted here — see the note on `inputUrl` below. Without the cloud's prompt the
 *     run stops here, before anything is spent.
 *  3. `hfMcpEstimate` — what it will cost, before anything is spent.
 *  4. `hfMcpGenerate` — case, product, view, all as `image_references`: the order the prompt
 *     names them in and the cloud sends them in, so either path produces the same picture.
 *  5. `hfMcpWait`, looping until terminal.
 *  6. `submitDeviceResult` on a completion — or `submitDeviceFailure` on `failed` / `nsfw` /
 *     `canceled`, or on any error after the job existed.
 *
 * The existing candidates poll then shows the result exactly as it shows a cloud-run one.
 *
 * **The one rule that matters most here: a non-completed job is NEVER reported as a success.**
 * `failed`, `nsfw` and `canceled` each post a distinct sentence to `/fail` and each throw. The
 * ONLY path that posts a result is a `completed` job with bytes actually downloaded.
 */
import type { GenerationJobView, GenerationOutput, GenerationPrintArea, JsonClient, PlatformIO } from "@genz/platform";
import { HttpError, higgsfieldMcp, type HiggsfieldMcpClient } from "@genz/platform";
import { fetchResultBytes } from "./candidates.js";
import { errorDetail } from "./errorCode.js";
import {
  DEFAULT_HF_FRAME,
  frameParams,
  listCreditsFor,
  noQuoteRefusal,
  overCeilingRefusal,
} from "./higgsfieldModels.js";
import { jobGone } from "./jobActions.js";
import {
  UNKNOWN_SUBMISSION_MESSAGE,
  clearLatch,
  markSubmitted,
  markSubmitting,
  newIdempotencyKey,
  readLatch,
  type SubmissionRecord,
} from "./deviceSubmissionLatch.js";
import { forgetRun, recallRun, rememberRun, runKey, storageAvailable } from "./deviceRunRegistry.js";

/**
 * Can this device protect a retry from being billed twice? (N7)
 *
 * Re-exported from here because this module is what the protection IS, so a caller asks the
 * device path one question instead of reaching past it into the registry.
 */
export { storageAvailable };

/** Default Higgsfield model. */
export const DEFAULT_HF_MODEL = "nano_banana_2";

/** Longest this waits for one job before giving up. `jobs_wait` caps a single call at 15 s,
 *  so this is the number of rounds, not one long request. */
export const DEVICE_WAIT_BUDGET_MS = 10 * 60 * 1000;

/**
 * Floor on the pause between `jobs_wait` rounds.
 *
 * `poll_after_seconds` is ADVISORY and the vendor may send `0`, or nothing at all. Honouring
 * that literally turns the wait into a hot loop against a paid API for the whole minute a
 * generation takes — and `jobs_wait` may itself return immediately when nothing has changed.
 * Two seconds is well inside the vendor's own 15 s per-call ceiling and costs at most two
 * seconds of latency on the finished image.
 */
export const MIN_WAIT_PAUSE_MS = 2000;

/** What the caller sees while a device run progresses. */
export type DeviceGenerationPhase =
  | "enqueued"
  | "estimating"
  | "submitted"
  | "waiting"
  | "uploading"
  | "done";

export interface RunDeviceGenerationInput {
  client: JsonClient;
  /** The case reference (PNG/JPEG bytes) — the same bytes the cloud path enqueues. */
  inputPng: Uint8Array;
  inputMime?: string;
  /** The optional product reference — a photo of the real phone. */
  productPng?: Uint8Array;
  productMime?: string;
  /** Case references 2..5, stored beside the first and sent after it. */
  moreCaseImages?: { bytes: Uint8Array; mime?: string }[];
  /** View VALUES (`back`, `front`, …) — one job per view, as the cloud path does. */
  views: string[];
  /**
   * Option-choice ids for a view's job. A function when the set differs per view — the
   * Generate panel sends the VIEW's own choice id plus the shared modifiers, exactly as the
   * cloud path does, and passing one flat list would drop the view choice.
   */
  optionChoiceIds?: string[] | ((view: string) => string[]);
  templateId?: string;
  brandId?: string;
  details?: {
    productType?: string; brand?: string; model?: string; name?: string;
    phoneModelId?: string; caseStyleId?: string; baseType?: string;
  };
  printArea?: GenerationPrintArea;
  output?: GenerationOutput;
  /** The Generate press's run id, sent with every view's job. */
  runId?: string;
  model?: string;
  params?: Record<string, unknown>;
  /** Injectable for tests; defaults to the process-wide desktop bridge. */
  mcp?: HiggsfieldMcpClient;
  onPhase?: (view: string, phase: DeviceGenerationPhase) => void;
  /** Fired the moment the CLOUD job exists, before a single credit is spent. The Generate
   *  panel registers the row here so its existing poll drives the UI while this runs — a
   *  device run must not look like nothing is happening for the minute it takes. */
  onJobCreated?: (view: string, job: GenerationJobView) => void;
  /** Operator log — a failed cost quote, a resumed submission. */
  logger?: { log: (level: string, message: string) => void };
  /**
   * Retry an EXISTING cloud job instead of creating one (F1).
   *
   * A retry must never enqueue a second job, and must never resubmit a generation whose first
   * attempt may already have been billed. With this set, `enqueueGeneration` is skipped and
   * the submission latch decides what happens: resume the vendor jobs already recorded, or
   * refuse because the outcome is unknown.
   */
  resumeJobId?: string;
  /** Desktop only: the result is downloaded by the platform instead of the web view. */
  downloadBytes?: PlatformIO["downloadBytes"];
  /** Injectable for tests; defaults to `candidates.ts`'s result fetch. */
  fetchBytes?: typeof fetchResultBytes;
  /** Injectable for tests; defaults to a real `setTimeout`. The pause between `jobs_wait`
   *  rounds is a behaviour worth asserting, and asserting it with real time costs seconds. */
  sleep?: (ms: number) => Promise<void>;
}

export interface DeviceGenerationOutcome {
  /** The cloud job, as it stands after the result (or the failure) was posted. */
  job: GenerationJobView;
  view: string;
  /** Credits the estimate quoted (not what was billed — the vendor is authoritative). */
  estimatedCredits?: number;
}

/**
 * Did this failure leave the design STUCK behind an unknown submission? (N6)
 *
 * When it did, no amount of pressing Generate will help: the latch refuses, by design,
 * because only the operator can find out whether their Higgsfield account was billed. The UI
 * needs to recognise that one case so it can offer the way out.
 */
export function isStuckOnUnknownSubmission(e: unknown): e is DeviceGenerationError {
  return e instanceof DeviceGenerationError && e.message.includes(UNKNOWN_SUBMISSION_MESSAGE);
}

/**
 * The operator has checked their Higgsfield account and says it is safe to run again (N6).
 *
 * Clears BOTH halves — the submission latch on the cloud job and the design-to-job mapping —
 * because leaving either one would keep the next press pinned to the same refusal. Deliberately
 * an explicit action with a sentence attached: it is the moment the operator accepts the risk
 * of a second charge, and nothing in this app may take it on their behalf.
 */
export async function clearStuckDeviceRun(
  jobId: string,
  inputPng: Uint8Array,
  view: string,
): Promise<void> {
  clearLatch(jobId);
  forgetRun(await runKey(inputPng, view));
}

/**
 * The views of this design whose device run may already be paid for: a submission latch on its
 * job, or a job the cloud reports as processing. Starting a new cloud job for such a view could
 * charge twice, whichever wallet it runs on (F1).
 */
export async function unsettledDeviceRuns(
  client: JsonClient,
  inputPng: Uint8Array,
  views: string[],
): Promise<{ jobId: string; view: string }[]> {
  const unsettled: { jobId: string; view: string }[] = [];
  for (const view of views) {
    const key = await runKey(inputPng, view);
    const jobId = recallRun(key);
    if (!jobId) continue;
    const job = await readJobIfExists(client, jobId);
    // A removed job with a paid vendor job stays: the next run posts it, so the cloud logs the spend.
    if (job ? settledOnCloud(job) : !collectable(readLatch(jobId))) {
      clearLatch(jobId);
      forgetRun(key);
      continue;
    }
    if (readLatch(jobId) || job?.status === "processing") unsettled.push({ jobId, view });
  }
  return unsettled;
}

/** A device run's requests outlive a sign-out: cancelling one mid-run could strand a paid image. */
const KEEP = { keepOnSignOut: true };

/** The job, or `undefined` when the cloud no longer has it: a mapping to it protects nothing. */
async function readJobIfExists(client: JsonClient, jobId: string): Promise<GenerationJobView | undefined> {
  try {
    return await client.getGenerationJob(jobId, KEEP);
  } catch (e) {
    if (jobGone(e)) return undefined;
    throw e;
  }
}

/** The N6 refusal for an unsettled run, shaped so `isStuckOnUnknownSubmission` offers the door. */
export function unsettledRunError(run: { jobId: string; view: string }): DeviceGenerationError {
  const e = new DeviceGenerationError(
    `Higgsfield may already be generating this image and the ${UNKNOWN_SUBMISSION_MESSAGE}.`,
    run.jobId,
    true,
  );
  e.view = run.view;
  return e;
}

/** A failure that already reached the cloud job's `/fail` route, so the caller must not post
 *  it again. Carries the sentence the operator sees. */
export class DeviceGenerationError extends Error {
  /**
   * The VIEW this failure belongs to (R3). Set by `runDeviceGeneration` for every failure of
   * a view, so a caller running several views never has to guess which one broke — guessing
   * `views[0]` is how the way out of a stuck run cleared the wrong view.
   */
  view?: string;

  constructor(message: string, readonly jobId?: string, readonly reported = false) {
    super(message);
    this.name = "DeviceGenerationError";
  }
}

/** A paid image whose collection failed on a resume too; `abandonPaidDeviceRun` gives it up. */
export class PaidCollectError extends DeviceGenerationError {
  constructor(message: string, jobId: string) {
    super(message, jobId, true);
    this.name = "PaidCollectError";
  }
}

/** The cloud job was cancelled: the run ends, nothing more is sent, and the design is free. */
function cancelledRun(jobId: string, key: string): DeviceGenerationError {
  clearLatch(jobId);
  forgetRun(key);
  return new DeviceGenerationError("This generation was cancelled.", jobId, true);
}

/** The cloud refused a result because its job was cancelled; it has logged the spend. */
const refusedAsCancelled = (e: unknown): boolean =>
  e instanceof HttpError && e.status === 409 && errorDetail(e) === "generation_cancelled";

/** The cloud refused because the job is finished, cancelled or removed: nothing more can be posted. */
const refusedAsFinished = (e: unknown): boolean =>
  e instanceof HttpError && e.status === 409 && errorDetail(e) === "job already finished";

/**
 * The operator gives up a paid image that cannot be collected (M3). The cloud job is failed —
 * no reaper ever closes a device job — and the design is free for a new, charged run.
 */
export async function abandonPaidDeviceRun(
  client: JsonClient,
  jobId: string,
  inputPng: Uint8Array,
  view: string,
): Promise<void> {
  try {
    await client.submitDeviceFailure(jobId, "The operator gave up collecting this paid Higgsfield image.");
  } catch (e) {
    if (!refusedAsFinished(e) && !jobGone(e)) throw e;
  }
  clearLatch(jobId);
  forgetRun(await runKey(inputPng, view));
}

/** Run every requested view, in order. Stops at the first failure, like the cloud path. */
export async function runDeviceGeneration(
  input: RunDeviceGenerationInput,
): Promise<DeviceGenerationOutcome[]> {
  const out: DeviceGenerationOutcome[] = [];
  for (const view of input.views) {
    try {
      out.push(await runOneView(input, view));
    } catch (e) {
      // ONE place that names the view, whichever of the many throw sites in `runOneView`
      // raised — the latch refusal, a settled failure, a vendor error.
      if (e instanceof DeviceGenerationError && !e.view) e.view = view;
      throw e;
    }
  }
  return out;
}

/** The company's per-run credit ceiling. An older cloud publishes none: nothing is compared. */
async function perRunCeiling(client: JsonClient): Promise<number | undefined> {
  const caps = await client.customizationCapabilities?.().catch(() => undefined);
  return caps?.shared_generation_max_credits_per_stage ?? undefined;
}

async function runOneView(
  input: RunDeviceGenerationInput,
  view: string,
): Promise<DeviceGenerationOutcome> {
  const {
    client,
    inputPng,
    inputMime,
    optionChoiceIds,
    templateId,
    brandId,
    details,
    printArea,
    output,
    runId,
    onPhase,
    onJobCreated,
    logger,
  } = input;
  const mcp = input.mcp ?? higgsfieldMcp();
  const fetchBytes = input.fetchBytes ?? ((url: string) => fetchResultBytes(url, input.downloadBytes));
  const model = input.model ?? DEFAULT_HF_MODEL;

  // 1 — the cloud creates the record. If THIS fails, no MCP call is made and nothing is spent:
  // a device run without a job to attach the result to would be an image nobody can reach.
  //
  // A RETRY (`resumeJobId`) skips this: enqueuing again would leave an orphan job behind and,
  // worse, would give the submission latch a new key to be ignorant about.
  // N1 — is this press a RETRY of one already in flight? The operator can only repeat what
  // they can see: this design, this view. Without this lookup the latch below is looked up
  // under a job id that has never been submitted, and the protection is worth nothing.
  const key = await runKey(inputPng, view);
  const resumeId = input.resumeJobId ?? recallRun(key);

  const enqueueView = async (): Promise<GenerationJobView> => {
    const enqueued = await client.enqueueGeneration(inputPng, [view], {
      optionChoiceIds: typeof optionChoiceIds === "function" ? optionChoiceIds(view) : optionChoiceIds,
      templateId,
      brandId,
      mime: inputMime,
      details,
      moreCaseImages: input.moreCaseImages,
      productImage: input.productPng ? { bytes: input.productPng, mime: input.productMime } : undefined,
      executor: "device",
      printArea,
      output,
      runId,
    });
    const first = enqueued[0];
    if (!first) throw new DeviceGenerationError("The cloud did not create a generation job.");
    // Bound to the design + view BEFORE anything is spent, so the next press finds it.
    rememberRun(key, first.id);
    return first;
  };

  let created: GenerationJobView;
  let removed = false;
  if (resumeId) {
    const found = await readJobIfExists(client, resumeId);
    removed = !found;
    // A removed job reads as a cancelled one: with a paid vendor job the run goes on to post
    // the result, which is how the cloud logs the spend; with none it ends here.
    created = found ?? ({ id: resumeId, status: "cancelled" } as GenerationJobView);
    // N2 — the CLOUD's own record of this job settles most retries before a credit is at
    // risk. Re-reading it and then submitting anyway is how the commonest retry (the run
    // finished, the reply to the UI was lost) becomes a second charge for an image that is
    // already sitting on the job.
    const settled = settledOutcome(created, view, key);
    if (settled) return settled;
    // H1 — a failed cloud job cannot take a result, but its paid vendor job can still be
    // collected: it moves to a new job, and nothing is generated again.
    if (created.status === "failed") created = carryPaidRun(created.id, await enqueueView());
  } else {
    created = await enqueueView();
  }
  onPhase?.(view, "enqueued");
  if (!removed) onJobCreated?.(view, created);

  // F1 — has this cloud job already been sent to Higgsfield? `hfMcpGenerate` spends credits,
  // and a lost reply does not mean the vendor did not start (and bill) the job.
  const latched = readLatch(created.id);
  // N2 — a job the cloud says is PROCESSING was started by somebody: this app, on another
  // run or before a crash. With no latch record to say whether it was billed, the only
  // honest answer is the same one the `submitting` latch gives.
  if (created.status === "processing" && !latched) {
    throw new DeviceGenerationError(
      `Higgsfield may already be generating this image and the ${UNKNOWN_SUBMISSION_MESSAGE}.`,
      created.id,
    );
  }
  if (latched?.state === "submitting") {
    // A dead end on purpose: this client cannot ask "what did I start a minute ago"
    // (`transactions` reports spends after the fact, not in-flight jobs), so guessing here
    // would cost the operator real money. Tell them to look.
    throw new DeviceGenerationError(
      `Higgsfield was already asked to generate this image and the ${UNKNOWN_SUBMISSION_MESSAGE}.`,
      created.id,
    );
  }

  const cancelled = () => cancelledRun(created.id, key);
  // From here on, every failure is REPORTED to the job — an abandoned job stays "queued"
  // forever in the operator's list and no worker will ever pick it up.
  try {
    let estimatedCredits: number | undefined;
    let jobIds: string[];
    const collecting = collectable(latched);

    if (latched?.state === "submitted" && latched.jobIds?.length) {
      // The submission succeeded and only the WAIT was interrupted. Resuming is free; the
      // expensive mistake would be to generate the same image again.
      jobIds = latched.jobIds;
      logger?.log(
        "info",
        `Higgsfield · resuming job ${created.id} on the generation already submitted ` +
          `(${jobIds.join(", ")}).`,
      );
      onPhase?.(view, "submitted");
    } else {
      // 2 — what the cloud resolved for this job.
      const job = await readJobIfExists(client, created.id);
      // Cancelled or removed before the submit: nothing was sent, and nothing may be.
      if (!job || job.status === "cancelled") throw cancelled();
      const prompt = resolvePrompt(job);
      const inputUrls = pictureUrls(job);
      if (inputUrls.length === 0) {
        throw new DeviceGenerationError(
          "The cloud did not return a link to the uploaded design, so there is nothing to send to Higgsfield.",
          created.id,
        );
      }

      // The frame the cloud recorded on the job; an older cloud names none.
      const params = input.params ?? frameParams(model, {
        resolution: job.resolution ?? DEFAULT_HF_FRAME.resolution,
        quality: job.quality ?? DEFAULT_HF_FRAME.quality,
      });

      // 3 — quote the cost. `get_cost` submits nothing, so this is safe to do every time.
      onPhase?.(view, "estimating");
      try {
        estimatedCredits = (await mcp.hfMcpEstimate(model, params)).credits;
      } catch (e) {
        // A quote that fails is not a reason to refuse a run whose price is recorded — the
        // vendor will refuse it for real if the credits are not there.
        logger?.log("warn", `Higgsfield cost estimate failed — ${errText(e)}`);
        if (listCreditsFor(model, params) === undefined) {
          throw new DeviceGenerationError(noQuoteRefusal(model), created.id);
        }
      }
      const credits = estimatedCredits ?? listCreditsFor(model, params);
      const ceiling = await perRunCeiling(client);
      if (credits !== undefined && ceiling !== undefined && credits > ceiling) {
        throw new DeviceGenerationError(overCeilingRefusal(credits, ceiling), created.id);
      }

      // 4 — submit. Case, product, view — the order the prompt names.
      //
      // The latch is written BEFORE the request leaves. If the process dies between these two
      // lines, the record is what tells the next attempt "we do not know whether this was
      // billed" — which is the whole reason the latch exists.
      const key = newIdempotencyKey();
      markSubmitting(created.id, key);
      const submitted = await mcp.hfMcpGenerate({
        model,
        prompt,
        // The key travels with the request so a spend in `transactions` can be matched to a
        // job by hand. The vendor does not promise to honour it; it is a correlation id.
        params: { ...params, client_request_id: key },
        inputUrls,
      });
      jobIds = submitted.jobIds;
      markSubmitted(created.id, key, jobIds);
      onPhase?.(view, "submitted");
    }

    // 5 — wait. From here the image may be paid for: only a vendor refusal ends the run, and
    // every other failure keeps the latch and the mapping so the next press collects it (H1).
    onPhase?.(view, "waiting");
    const status = await whilePaid(
      () => waitForTerminal(mcp, jobIds, DEVICE_WAIT_BUDGET_MS, input.sleep),
      created.id,
      collecting,
    );
    const reason = failureSentence(status.status, status.error);
    if (reason) {
      // Refused: nothing left for the latch to protect. R2 — nor for the run mapping. Kept,
      // it would make every later press RESUME this job, and a resumed failed job re-raises
      // its failure: the operator could never try again. The next press is a new run.
      clearLatch(created.id);
      forgetRun(key);
      throw new DeviceGenerationError(reason, created.id);
    }

    // 6 — the bytes go to the cloud, and only now is this a success.
    onPhase?.(view, "uploading");
    const { bytes, mediaType } = await whilePaid(
      () => fetchBytes(firstResultUrl(status.urls)),
      created.id,
      collecting,
    );
    const finished = await whilePaid(
      () => client.submitDeviceResult(created.id, bytes, {
        provider: "higgsfield-mcp",
        model,
        credits: estimatedCredits,
        mediaType,
      }).catch((e: unknown) => {
        // A lost reply to a stored result: the cloud is settled, and so is this run.
        if (refusedAsFinished(e)) return created;
        throw refusedAsCancelled(e) ? cancelled() : e;
      }),
      created.id,
      collecting,
    );
    // The operator has their image. A later press is a deliberate request for a NEW one and
    // must not be turned into a replay of this run.
    clearLatch(created.id);
    forgetRun(key);
    onPhase?.(view, "done");
    return { job: finished, view, estimatedCredits };
  } catch (e) {
    if (e instanceof DeviceGenerationError && e.reported) throw e;
    const message = e instanceof Error ? e.message : String(e);
    // Best-effort: if the cloud is unreachable too, the original failure is still the one
    // the operator needs, so a failing `/fail` must not replace it.
    try {
      await client.submitDeviceFailure(created.id, message);
    } catch {
      /* the message below is still raised */
    }
    throw new DeviceGenerationError(message, created.id, true);
  }
}

/**
 * A step after the submission. Its failure is left UNREPORTED to `/fail`: the image may be paid
 * for, and a failed cloud job would turn the next press into a second charge, not a collection.
 * A COLLECTION that fails as well may never succeed (an expired link, a refused host), so it
 * offers the way out instead of "press again" for as long as the latch lives (M3).
 */
async function whilePaid<T>(step: () => Promise<T>, jobId: string, collecting: boolean): Promise<T> {
  try {
    return await step();
  } catch (e) {
    if (e instanceof DeviceGenerationError && e.reported) throw e;
    if (collecting) {
      throw new PaidCollectError(
        `Collecting this paid Higgsfield image failed again (${errText(e)}). Press Generate to ` +
          "try once more, or give it up: the paid image is abandoned and the next run is charged again.",
        jobId,
      );
    }
    throw new DeviceGenerationError(
      `This image may already be paid for on Higgsfield, but the run stopped (${errText(e)}). ` +
        "Press Generate again to collect it; it will not be charged again.",
      jobId,
      true,
    );
  }
}

function firstResultUrl(urls: string[]): string {
  if (!urls[0]) throw new Error("Higgsfield reported the image was finished but sent no link to it");
  return urls[0];
}

/** Move a paid vendor job's latch from a failed cloud job onto `fresh`, which will collect it. */
function carryPaidRun(failedJobId: string, fresh: GenerationJobView): GenerationJobView {
  const latch = readLatch(failedJobId)!;
  markSubmitted(fresh.id, latch.key, latch.jobIds!);
  clearLatch(failedJobId);
  return fresh;
}

/** A latch whose vendor job was accepted: the image can still be collected. */
function collectable(latch: SubmissionRecord | undefined): boolean {
  return latch?.state === "submitted" && !!latch.jobIds?.length;
}

/**
 * Has the CLOUD already settled this job? (N2)
 *
 * `undefined` means "not finished — carry on". The two settled arms are deliberately
 * asymmetric: a completion is RETURNED (nothing more to do, and nothing to spend), while a
 * recorded failure is re-raised WITHOUT posting to `/fail` again, because the reason is
 * already on the job and a duplicate would be noise on the operator's row.
 */
function settledOutcome(
  job: GenerationJobView,
  view: string,
  key: string,
): DeviceGenerationOutcome | undefined {
  if (hasResult(job)) {
    clearLatch(job.id);
    forgetRun(key);
    return { job, view };
  }
  // A cancelled job with a paid vendor job runs on: posting its result is how the cloud logs
  // the spend.
  if (job.status === "cancelled" && !collectable(readLatch(job.id))) throw cancelledRun(job.id, key);
  if (job.status === "failed" && !collectable(readLatch(job.id))) {
    clearLatch(job.id);
    // A recorded failure is settled: the operator may legitimately try again from scratch.
    forgetRun(key);
    throw new DeviceGenerationError(
      job.error || "Higgsfield could not generate this image.",
      job.id,
      // Already recorded on the job — the caller must not post it a second time.
      true,
    );
  }
  return undefined;
}

/** Sent to `/fail` when the cloud has no prompt for the job; the code leads for `errorCode`. */
const PROMPT_UNAVAILABLE =
  "prompt_unavailable: the cloud sent no prompt for this job, so nothing was sent to Higgsfield.";

/**
 * The prompt to send: the cloud's, or none at all. A local copy would bypass a guardrail block
 * and the admin-edited prompt, and it would be billed.
 *
 * Higgsfield's `generate_image` takes ONE string, so the split pair is joined exactly as
 * `router.py` builds `promptText` — f"{system}\n\n{user}".strip().
 */
export function resolvePrompt(job: GenerationJobView): string {
  const joined = `${job.promptSystem ?? ""}\n\n${job.promptUser ?? ""}`.trim() || job.promptText;
  if (job.promptStatus !== "ready" || !joined) throw new DeviceGenerationError(PROMPT_UNAVAILABLE, job.id);
  return joined;
}

/** The pictures to import, IN SEND ORDER: every case reference, product (optional), view. */
export function pictureUrls(job: GenerationJobView): string[] {
  const cases = job.inputUrls?.length ? job.inputUrls : [job.inputUrl];
  return [...cases, job.productUrl, job.viewUrl].filter((u): u is string => !!u);
}

function hasResult(job: GenerationJobView): boolean {
  const finished = job.status === "completed" || job.status === "approved" || job.status === "needs_review";
  return finished && !!job.resultUrl;
}

/** Does the cloud's own record settle this job, as `settledOutcome` would? */
function settledOnCloud(job: GenerationJobView): boolean {
  const stopped = job.status === "failed" || job.status === "cancelled";
  return hasResult(job) || (stopped && !collectable(readLatch(job.id)));
}

/**
 * The operator-facing sentence for a terminal state that is NOT a completion, or `undefined`
 * for one that is. This is the single place the "never claim success" rule is decided.
 */
export function failureSentence(
  status: string,
  vendorReason?: string,
): string | undefined {
  const suffix = vendorReason ? ` (${vendorReason})` : "";
  switch (status) {
    case "completed":
      return undefined;
    case "nsfw":
      return `Higgsfield's content check refused this image. Nothing was generated${suffix}.`;
    case "canceled":
      return `The Higgsfield job was cancelled before it finished${suffix}.`;
    case "failed":
      return `Higgsfield could not generate this image${suffix}.`;
    default:
      // An unknown status is a failure, not a success: guessing the other way is how a
      // moderated result reaches a customer's print file.
      return `Higgsfield returned an unexpected job state "${status}"${suffix}.`;
  }
}

/** Poll `jobs_wait` until the job is terminal or the budget runs out. */
async function waitForTerminal(
  mcp: HiggsfieldMcpClient,
  jobIds: string[],
  budgetMs = DEVICE_WAIT_BUDGET_MS,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((res) => setTimeout(res, ms)),
) {
  const deadline = Date.now() + budgetMs;
  for (;;) {
    const r = await mcp.hfMcpWait(jobIds);
    const job = r.jobs.find((j) => j.jobId === jobIds[0]) ?? r.jobs[0];
    if (!job) throw new DeviceGenerationError("Higgsfield lost track of the generation job.");
    if (r.allTerminal || isTerminalState(job.status)) return job;
    if (Date.now() >= deadline) {
      throw new DeviceGenerationError(
        "Higgsfield did not finish this image in time. It may still complete on higgsfield.ai.",
      );
    }
    // The vendor's hint when it gives one, but never faster than the floor.
    await sleep(Math.max((r.pollAfterSeconds ?? 0) * 1000, MIN_WAIT_PAUSE_MS));
  }
}

function isTerminalState(s: string): boolean {
  return s === "completed" || s === "failed" || s === "nsfw" || s === "canceled";
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
